// STEP1-OUTBOX (build order increment 1 B; integrations design §2.5, §2.6, §2.8; booking design
// §2 `email_outbox`): the outward-action seam's row contracts and the `external_action`
// payload. Every outward act an agent takes goes one path:
//   proposal (`external_action` approval, `change` = ExternalActionChange) → policy or approval →
//   `action_outbox` row → executor (per action and provider) → result → audit.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  ConnectionFamily,
  ConnectionGranteeKind,
  ConnectionGrantLevel,
  ExternalActionKind,
  OutboxStatus,
} from "./enums";
import { Email, LIMITS, Timestamp, text, Uuid } from "./primitives";

const stamps = { created_at: Timestamp, updated_at: Timestamp };

/** A domain name (`example.com`), lower-case. */
const Domain = z
  .string()
  .min(3)
  .max(253)
  .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, "a domain name");

/** `connection_grant.limits`: bounds on what the grant lets its grantee do. */
export const ConnectionGrantLimits = z
  .strictObject({
    /**
     * At most this many acts handed to the vendor through the connection per UTC day, counted on
     * the day of dispatch; a send whose answer was lost keeps counting, and a person's Retry of
     * one that may not be deduplicated counts again.
     */
    daily_acts: z.int().min(1).max(10_000).optional(),
    /** `mail.send`: every recipient's domain must be one of these. */
    recipient_domains: z.array(Domain).min(1).max(50).optional(),
    /**
     * GHL-PROVIDER (build order §3.1a, connector-mode CRM policy): `crm.write` fields always held
     * for a person's approval through this grant (absent: the vendor's standard identity fields).
     */
    locked_fields: z.array(z.string().min(1).max(LIMITS.key)).max(100).optional(),
    /**
     * `crm.write` fields an agent may write without an approval (besides notes, tasks and tags);
     * a locked field stays held whatever this says.
     */
    direct_fields: z.array(z.string().min(1).max(LIMITS.key)).max(100).optional(),
  })
  .meta({ id: "ConnectionGrantLimits" });
export type ConnectionGrantLimits = z.infer<typeof ConnectionGrantLimits>;

/** Who may use a connection, and how far (integrations §2.6). */
export const ConnectionGrant = z
  .object({
    company_id: Uuid,
    id: Uuid,
    connection_id: Uuid,
    grantee_kind: ConnectionGranteeKind,
    grantee_id: Uuid,
    level: ConnectionGrantLevel,
    families: z.array(ConnectionFamily),
    limits: ConnectionGrantLimits,
    granted_by_membership_id: Uuid,
    revoked_at: Timestamp.nullable(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "ConnectionGrant" });
export type ConnectionGrant = z.infer<typeof ConnectionGrant>;

// ---- the `external_action` payload ---------------------------------------------------------

const Recipients = z.array(Email).max(50);

/** `mail.send`: a mail, sent through the connection (Gmail, Outlook, SMTP, GHL later). */
export const MailSendRequest = z
  .strictObject({
    to: Recipients.min(1),
    cc: Recipients.optional(),
    bcc: Recipients.optional(),
    subject: text(998),
    body_markdown: z.string().min(1).max(LIMITS.longText),
    /** A reply: the provider's message id it answers (threaded by the executor). */
    in_reply_to: z.string().max(LIMITS.name).optional(),
  })
  .meta({ id: "MailSendRequest" });

/** `crm.write`: one write on a CRM object (GHL, HubSpot, the native CRM later). */
export const CrmWriteRequest = z
  .strictObject({
    object: z.string().min(1).max(LIMITS.key),
    // GHL-PROVIDER: `tag` adds and removes tags (`fields: {add?, remove?}`): a vendor that
    // replaces every tag on an update (GHL) is never written a whole tag list
    op: z.enum(["create", "update", "note", "task", "stage_move", "tag"]),
    record_id: z.string().max(LIMITS.name).optional(),
    fields: z
      .record(z.string().max(LIMITS.key), z.unknown())
      .refine((r) => Object.keys(r).length <= LIMITS.recordKeys, "at most 50 fields")
      .meta({ maxProperties: LIMITS.recordKeys }),
  })
  .meta({ id: "CrmWriteRequest" });

/** `message.send`: a message on a channel (SMS, WhatsApp, a CRM conversation). */
export const MessageSendRequest = z
  .strictObject({
    to: z.string().min(1).max(LIMITS.name),
    channel: z.string().max(LIMITS.key).optional(),
    text: z.string().min(1).max(LIMITS.text),
    template: z.string().max(LIMITS.key).optional(),
    /** GHL-PROVIDER: an email through a CRM conversation (`channel: email`) has a subject. */
    subject: text(998).optional(),
  })
  .meta({ id: "MessageSendRequest" });

/** `api.request`: one call on an API-key connection's host (a path, never a URL). */
export const ApiRequestRequest = z
  .strictObject({
    method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
    path: z
      .string()
      .min(1)
      .max(1024)
      // One leading slash, never `//host` nor a backslash (a URL resolver could read either as
      // another host); executors still check the resolved URL's origin before adding the key
      .regex(/^\/(?![/\\])[^\s\\]*$/, "a path on the connection's host, starting with one /"),
    body: z
      .record(z.string().max(LIMITS.key), z.unknown())
      .refine((r) => Object.keys(r).length <= LIMITS.recordKeys, "at most 50 keys")
      .meta({ maxProperties: LIMITS.recordKeys })
      .optional(),
  })
  .meta({ id: "ApiRequestRequest" });

/** The object an act is timed to (a booking: reminders move with a reschedule). */
export const OutboxAnchor = z
  .strictObject({
    kind: z.enum(["booking", "calendar_event", "task"]),
    id: Uuid,
    offset_min: z.int().min(-525_600).max(525_600),
  })
  .meta({ id: "OutboxAnchor" });
export type OutboxAnchor = z.infer<typeof OutboxAnchor>;

const common = {
  /** The connection it goes through; absent or null: a dry run (logged only, nothing sent). */
  connection_id: Uuid.nullable().optional(),
  /** Not before this time (null: as soon as it is approved). */
  send_at: Timestamp.nullable().optional(),
  anchor: OutboxAnchor.nullable().optional(),
};

/**
 * An `external_action` approval's `proposal.change`: the action (family sub-kind) and its exact
 * request. The executor replays it as approved, through the grant as it stands at execution.
 */
export const ExternalActionChange = z
  .discriminatedUnion("action", [
    z.strictObject({ action: z.literal("mail.send"), request: MailSendRequest, ...common }),
    z.strictObject({ action: z.literal("crm.write"), request: CrmWriteRequest, ...common }),
    z.strictObject({ action: z.literal("message.send"), request: MessageSendRequest, ...common }),
    z.strictObject({ action: z.literal("api.request"), request: ApiRequestRequest, ...common }),
  ])
  .meta({ id: "ExternalActionChange" });
export type ExternalActionChange = z.infer<typeof ExternalActionChange>;

/** Which tool family an action belongs to (the grant's `families`). */
export const ACTION_FAMILY: Record<
  z.infer<typeof ExternalActionKind>,
  z.infer<typeof ConnectionFamily>
> = {
  "mail.send": "mail",
  "crm.write": "crm",
  "message.send": "message",
  "api.request": "api",
};

// ---- the outbox row ------------------------------------------------------------------------

/** What the executor answered (a vendor id; never a request or response body). */
export const OutboxResult = z
  .strictObject({
    vendor_id: z.string().nullable(),
    dry_run: z.boolean(),
    provider: z.string(),
  })
  .meta({ id: "OutboxResult" });

/**
 * CORE-RECEIPTS (core delivery plan item 4): why an act went out and through what — the approver,
 * the agent policy that made it an approval, the rulings and memory records behind it, and the
 * provider's message id once sent. Ids and names only, never the act's content.
 */
export const OutboxReceipt = z
  .strictObject({
    approval_id: Uuid.nullable(),
    approver: z.strictObject({
      membership_id: Uuid.nullable(),
      name: z.string().nullable(),
      seat_id: Uuid.nullable(),
    }),
    approved_at: Timestamp.nullable(),
    /** The policy source (null: a person asked, no agent policy applied). */
    policy: z
      .strictObject({ level: z.string(), scope_id: z.string().nullable(), mode: z.string() })
      .nullable(),
    precedent_ids: z.array(z.string()),
    record_ids: z.array(z.string()),
    run_id: Uuid.nullable(),
    executor: z.strictObject({
      mode: z.enum(["live", "log_only"]),
      provider: z.string(),
      /** Why it is logged only (no connected provider, no grant, the flag off). */
      why: z.string().nullable(),
    }),
    provider_message_id: z.string().nullable(),
    queued_at: Timestamp,
    sent_at: Timestamp.nullable(),
  })
  .meta({ id: "OutboxReceipt" });
export type OutboxReceipt = z.infer<typeof OutboxReceipt>;

/** One outward act in the outbox. */
export const ActionOutboxItem = z
  .object({
    company_id: Uuid,
    id: Uuid,
    action: ExternalActionKind,
    approval_id: Uuid.nullable(),
    requested_by_membership_id: Uuid.nullable(),
    connection_id: Uuid.nullable(),
    provider: z.string(),
    payload: z.record(z.string(), z.unknown()),
    idempotency_key: z.string(),
    send_at: Timestamp.nullable(),
    anchor: OutboxAnchor.nullable(),
    status: OutboxStatus,
    attempts: z.int().min(0),
    next_attempt_at: Timestamp,
    lease_until: Timestamp.nullable(),
    claim_token: Uuid.nullable(),
    dispatched_at: Timestamp.nullable(),
    dispatch_charges: z.int().min(0),
    delivery_unresolved: z.boolean(),
    unresolved_at: Timestamp.nullable(),
    last_error: z.string().nullable(),
    dead_reason: z.string().nullable(),
    result: OutboxResult.nullable(),
    sent_at: Timestamp.nullable(),
    cancelled_at: Timestamp.nullable(),
    receipt: OutboxReceipt.nullable(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "ActionOutboxItem" });
export type ActionOutboxItem = z.infer<typeof ActionOutboxItem>;
